import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import apiService from '../../Common/Configurations/APIService';
import config from '../../Common/Configurations/APIConfig';
import './UmrahScreen.css'; 
import DefaultData from '../../CompanyData/DefaultData';

// Helper function to format dates
const formatDateSimple = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};


const UmrahPackageCard = ({ pkg }) => {
    const fallbackImages = [
        'https://images.unsplash.com/photo-1604424343191-584347898516?q=80&w=1974',
        'https://images.unsplash.com/photo-1610447955358-62d9ad47f892?q=80&w=1935',
        'https://images.unsplash.com/photo-1569426901844-327c5387431e?q=80&w=2070',
    ];
    
    const inclusionsList = pkg.includes ? pkg.includes.split(',').map(item => item.trim()) : [];

    return (
        <Card className="umrah-package-card h-100">
            <div className="package-image-container">
                <Card.Img variant="top" src={pkg.imageSrc || fallbackImages[0]} alt={pkg.packageName} />
                <div className="origin-badge">
                    <i className="ri-map-pin-2-fill"></i> From {pkg.fromOrigin}
                </div>
            </div>
            <Card.Body className="d-flex flex-column">
                <Card.Title className="fw-bold mb-2">
                    <Link to={`/umrah-details/${pkg.packageId}`} className="card-title-link">
                        {pkg.packageName}
                    </Link>
                </Card.Title>
                <div className="package-meta-info my-3">
                    <div className="meta-item" title="Package Duration">
                        <i className="ri-calendar-2-line"></i> Duration: <strong>{pkg.duration}</strong>
                    </div>
                    <div className="meta-item" title="Group Size">
                        <i className="ri-group-line"></i> Group Size: <strong>{pkg.noOfPersons} Persons</strong>
                    </div>
                </div>
                {inclusionsList.length > 0 && (
                    <div className="inclusions small mb-3">
                        <p className="mb-1"><strong>Includes:</strong> {inclusionsList.slice(0, 3).join(' • ')}{inclusionsList.length > 3 ? '...' : ''}</p>
                    </div>
                )}
                <div className="mt-auto">
                    <div className="package-start-date text-muted small mb-2">
                        <i className="ri-calendar-check-line"></i> Starts: {formatDateSimple(pkg.packageStartDate)}
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <span className="small text-muted">Starting from</span>
                            <div className="price-tag">
                                {pkg.currency} {pkg.price.toLocaleString('en-IN')}
                            </div>
                        </div>
                        <Button as={Link} to={`/umrah-details/${pkg.packageId}`} variant="primary">
                            View Details
                        </Button>
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};


const UmrahResults = () => {
    const [searchParams] = useSearchParams();
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const fromOrigin = searchParams.get('fromOrigin') || '';
    const month = searchParams.get('month') || '';
    const companyId = DefaultData.DEFAULTCOMPANYID;

    useEffect(() => {
        const getUmrahPackages = async () => {
            setIsLoading(true);
            try {
                const query = new URLSearchParams({
                    fromOrigin: fromOrigin,
                    month: month,
                }).toString();
                const response = await apiService.getAll(`${config.GETPACKAGESBYUMRAHSEARCH}/${companyId}?${query}`)
                setPackages(response.data); 
            } catch (error) {
                console.error("Failed to fetch Umrah search results:", error);
                setPackages([]); 
            } finally {
                setIsLoading(false);
            }
        };

        if (companyId) {
            getUmrahPackages();
        }
    }, [fromOrigin, month, companyId]);

    return (
        <div className="main-content">
            <div className="page-content">
                <Container className="my-1">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="section-title mb-0">Umrah Package Results</h2>
                        <Button as={Link} to="/umrahs" variant="outline-secondary">
                            <i className="ri-arrow-left-line me-2"></i>Back to Umrah Page
                        </Button>
                    </div>
                    
                    <p className="text-center text-muted mb-5">
                        Showing Umrah packages {fromOrigin && `departing from "${fromOrigin}"`} {month && `in "${month}"`}
                    </p>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Searching for your sacred journey...</p>
                        </div>
                    ) : packages.length > 0 ? (
                        <Row xs={1} md={2} lg={3} className="g-4">
                            {packages.map(pkg => (
                                <Col key={pkg.packageId}>
                                    <UmrahPackageCard pkg={pkg} />
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        <div className="text-center py-5">
                            <h4>No Umrah Packages Found</h4>
                            <p>We couldn't find any packages matching your criteria. Please try a different search.</p>
                            <Button as={Link} to="/umrahs">Back to Umrah Page</Button>
                        </div>
                    )}
                </Container>
            </div>
        </div>
    );
};

export default UmrahResults;